import objectHash from 'object-hash';
import { CSSProperties, ReactNode, useEffect, useMemo, useState } from 'react';
import { Panel, PanelGroup } from 'react-resizable-panels';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, ButtonGroup, Checkbox, Header, Modal, Popup, Segment, Tab, TabProps } from 'semantic-ui-react';
import Loader from '../../../components/loader';
import useModalState from '../../../hooks/useModalState';
import { CompanyType } from '../../../types/company.types';
import { getCompanyId } from '../../../utils/reportResourceUtil';
import CompanyRepo from '../../../utils/repository/companyRepo';
import ImportsRepo from '../../../utils/repository/importsRepo';
import Annotations from './components/annotations';
import { ImportTableStoreProvider } from './components/importTable/context/importTableContext';
import ImportTable from './components/importTable/importTable';
import { getImportTableStoreState } from './components/importTable/store/importTableStore/utils';
import InteractionBlocker from './components/interactionBlocker';
import MappingsTable from './components/mappings/components/mappingsTable';
import Mappings from './components/mappings/mappings';
import MasterTable from './components/masterTable';
import ResizeHandle from './components/resizeHandle';
import Resource from './components/Resource';
import { UTILITIES_PANEL_TAB, UTILITIES_PANEL_TABS } from './constants/generalConstants';
import useExtraction from './hooks/useExtraction';
import useSchema from './hooks/useSchema';
import './table.styles.css';
import './tableExtraction.styles.css';
import { getMasterDescriptorsMap } from './utils/general';
import AuthService from '../../../service/auth';
import { ROLES } from '../../../constnats/user';
import { AutomaticDataModesType } from './types';

const TableExtraction = () => {
    const navigate = useNavigate();
    const params = useParams();
    const importId = params.ID;
    const [automaticDataModes, setAutomaticDataModes] = useState<AutomaticDataModesType>({
        originalAutomaticDataOnly: false,
        latestAutomaticDataOnly: false,
    });
    const [simulated, setSimulated] = useState(AuthService.getUserId() == 11);
    const {
        extraction,
        isLoading: isExtractionLoading,
        setImportTable,
        setStates,
    } = useExtraction(importId, automaticDataModes);
    const deleteModalState = useModalState();
    const [company, setCompany] = useState<CompanyType | null>(null);
    const [activeTab, setActiveTab] = useState<(typeof UTILITIES_PANEL_TABS)[number]>(UTILITIES_PANEL_TAB.RESOURCE);
    const [schema, isSchemaLoading] = useSchema(extraction?.schemaId, importId, simulated);
    const [mainActiveTabIndex, setMainActiveTabIndex] = useState<number | string>(0);

    const mappingsPanelEnabled = mainActiveTabIndex === 0;
    const mappingsStyling = getMappingsStyling(mappingsPanelEnabled);

    const masterDescriptorsMap = useMemo(() => {
        let result = {};

        if (schema) {
            result = getMasterDescriptorsMap(schema.descriptors);
        }

        return result;
    }, [schema]);

    useEffect(() => {
        if (extraction && extraction.s3Key) {
            CompanyRepo.get(getCompanyId(extraction.s3Key), (response: CompanyType) => {
                setCompany(response);
            });
        }
    }, [extraction]);

    if (isExtractionLoading || isSchemaLoading) {
        return <Loader />;
    }

    if (!extraction) {
        return <Header>Extraction was not found</Header>;
    }

    const handleGoBack = () => {
        if (company) {
            navigate(`/admin/companies/${company.id}/reports/${extraction.schemaId}`);
        } else {
            navigate('/');
        }
    };

    const handleDeleteImport = () => {
        ImportsRepo.delete(importId, () => {
            handleGoBack();
        });
    };

    const handleMainActiveTabChange = (data: TabProps) => {
        if (data.activeIndex !== undefined) {
            setMainActiveTabIndex(data.activeIndex);

            if (data.activeIndex !== 0) {
                setActiveTab(UTILITIES_PANEL_TAB.RESOURCE);
            }
        }
    };

    const handleAutomaticDataModeChange = (prop: keyof AutomaticDataModesType) => {
        setAutomaticDataModes((prev) => ({
            latestAutomaticDataOnly: false,
            originalAutomaticDataOnly: false,
            [prop]: !prev[prop],
        }));
    };

    const panes = [
        getPane(
            'Annotations',
            <Annotations
                extraction={extraction}
                setExtraction={setStates}
                handleDeleteImport={deleteModalState.openModal}
            />
        ),
        getPane(
            'Mappings',
            <Mappings
                extraction={extraction}
                setExtraction={setStates}
                handleDeleteImport={deleteModalState.openModal}
                masterDescriptorsMap={masterDescriptorsMap}
            />
        ),
        getPane(
            'Import Table',
            <ImportTableStoreProvider
                key={objectHash(extraction.importTable)}
                initialState={getImportTableStoreState(
                    extraction.importTable.descriptors,
                    extraction.importTable.reports
                )}
            >
                <ImportTable
                    extraction={extraction}
                    setImportTable={setImportTable}
                    handleDeleteImport={deleteModalState.openModal}
                    redirectBack={handleGoBack}
                />
            </ImportTableStoreProvider>
        ),
    ];

    const getUtilityTabComponent = () => {
        switch (activeTab) {
            case UTILITIES_PANEL_TAB.RESOURCE:
                return <Resource s3Key={extraction.s3Key} />;
            case UTILITIES_PANEL_TAB.MAPPINGS:
                return (
                    <InteractionBlocker style={mappingsStyling.styles}>
                        <MappingsTable
                            handleImportDescriptorOnDropSwap={() => {}}
                            handleMasterDescriptorOnDropInsert={() => {}}
                            handleMasterDescriptorOnDropSwap={() => {}}
                            importDescriptors={extraction.importTable.descriptors}
                            reports={extraction.importTable.reports}
                            masterDescriptorsMap={masterDescriptorsMap}
                        />
                    </InteractionBlocker>
                );
            case UTILITIES_PANEL_TAB.MASTER_TABLE:
                return <MasterTable schema={schema!} />;
            default:
                return null;
        }
    };

    return (
        <>
            <ButtonGroup vertical className="resources-toggle-container">
                <Popup
                    content="Resource"
                    trigger={
                        <Button
                            primary={activeTab === UTILITIES_PANEL_TAB.RESOURCE}
                            secondary={activeTab !== UTILITIES_PANEL_TAB.RESOURCE}
                            icon="image"
                            onClick={() => setActiveTab(UTILITIES_PANEL_TAB.RESOURCE)}
                        />
                    }
                />
                <Popup
                    content="Mappings"
                    trigger={
                        <Button
                            disabled={!mappingsPanelEnabled}
                            primary={activeTab === UTILITIES_PANEL_TAB.MAPPINGS}
                            secondary={activeTab !== UTILITIES_PANEL_TAB.MAPPINGS}
                            icon="map"
                            onClick={() => setActiveTab(UTILITIES_PANEL_TAB.MAPPINGS)}
                        />
                    }
                />
                <Popup
                    content="Client Table"
                    trigger={
                        <Button
                            primary={activeTab === UTILITIES_PANEL_TAB.MASTER_TABLE}
                            secondary={activeTab !== UTILITIES_PANEL_TAB.MASTER_TABLE}
                            icon="table"
                            onClick={() => setActiveTab(UTILITIES_PANEL_TAB.MASTER_TABLE)}
                        />
                    }
                />
            </ButtonGroup>
            <div className="flex flex-align-center table-extraction__header__container">
                <Header
                    onClick={handleGoBack}
                    className="table-extraction__header ml-auto mr-auto cursor-pointer"
                    as="h2"
                >
                    {company?.name}
                </Header>
            </div>
            {AuthService.hasGlobalRole(ROLES.ADMIN) && (
                <Segment>
                    <Header as="h4">Automatic Data Modes</Header>
                    <div className='flex gap-10'>
                        <Checkbox
                            checked={automaticDataModes.latestAutomaticDataOnly}
                            label="Latest Automatic Data"
                            onChange={() => handleAutomaticDataModeChange('latestAutomaticDataOnly')}
                        />
                        <Checkbox
                            checked={automaticDataModes.originalAutomaticDataOnly}
                            label="Original Automatic Data"
                            onChange={() => handleAutomaticDataModeChange('originalAutomaticDataOnly')}
                        />
                        <Checkbox
                            checked={simulated}
                            onChange={() => setSimulated((prev) => !prev)}
                            label="Simulated Schema"
                        />
                    </div>
                   
                </Segment>
            )}
            <PanelGroup direction="horizontal">
                <Panel minSize={20}>
                    <Tab onTabChange={(_, data) => handleMainActiveTabChange(data)} panes={panes} />
                </Panel>
                <ResizeHandle />
                <Panel>{getUtilityTabComponent()}</Panel>
            </PanelGroup>
            <Modal size="mini" open={deleteModalState.open} onClose={deleteModalState.closeModal}>
                <Modal.Header>Delete Import</Modal.Header>
                <Modal.Content>
                    <p>Are you sure you want to delete the import ?</p>
                    <p>All data associated with the import will be deleted!</p>
                    <p>This action cannot be undone!</p>
                </Modal.Content>
                <Modal.Actions>
                    <Button negative onClick={handleDeleteImport}>
                        DELETE
                    </Button>
                    <Button positive onClick={deleteModalState.closeModal}>
                        CANCEL
                    </Button>
                </Modal.Actions>
            </Modal>
        </>
    );
};

export default TableExtraction;

function getPane(name: string, content: ReactNode) {
    return {
        menuItem: name,
        render: () => <Tab.Pane className="companyRootTab">{content}</Tab.Pane>,
    };
}

function getMappingsStyling(mappingsPanelEnabled: boolean): { styles: CSSProperties } {
    const styles: CSSProperties = {};

    if (mappingsPanelEnabled) {
        styles.marginTop = 225;
    }

    return { styles };
}
